/** @format */

import { createAsyncThunk } from "@reduxjs/toolkit";
import {
    submitCreateQuickDemoCircle,
    submitCreateCustomSandboxCircle,
    submitGetSandboxCircle,
    submitGetSandboxCircleByToken,
    submitUpdateMemberStatus,
    submitUpdateMemberDayStatus,
    submitCreateShareLink,
    submitConvertSandboxCircle,
} from "../services/sandboxService";
import {
    ICreateSandboxCirclePayload,
    SandboxContributionStatus,
    SandboxDayStatus,
} from "../../utility/types/sandboxTypes";

export interface IQuickDemoPayload {
    anonymousSessionId?: string | null;
    country?: string;
    currency?: string;
}

export const createQuickDemoCircle = createAsyncThunk(
    "sandbox/createQuickDemo",
    async (payload: IQuickDemoPayload | null | undefined, { rejectWithValue }) => {
        try {
            const { anonymousSessionId, country, currency } = payload ?? {};
            return await submitCreateQuickDemoCircle(anonymousSessionId, { country, currency });
        } catch (error: any) {
            return rejectWithValue(error.message || "Failed to create demo circle.");
        }
    },
);

export const createCustomSandboxCircle = createAsyncThunk(
    "sandbox/createCustom",
    async (payload: ICreateSandboxCirclePayload, { rejectWithValue }) => {
        try {
            return await submitCreateCustomSandboxCircle(payload);
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to create sandbox circle.",
            );
        }
    },
);

export const fetchSandboxCircle = createAsyncThunk(
    "sandbox/fetchById",
    async (sandboxCircleId: string, { rejectWithValue }) => {
        try {
            return await submitGetSandboxCircle(sandboxCircleId);
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to fetch sandbox circle.",
            );
        }
    },
);

export const fetchSandboxCircleByToken = createAsyncThunk(
    "sandbox/fetchByToken",
    async (shareToken: string, { rejectWithValue }) => {
        try {
            return await submitGetSandboxCircleByToken(shareToken);
        } catch (error: any) {
            return rejectWithValue(
                error.message || "This sandbox preview is no longer available.",
            );
        }
    },
);

export const updateSandboxMemberStatus = createAsyncThunk(
    "sandbox/updateMemberStatus",
    async (
        {
            sandboxCircleId,
            memberId,
            contributionStatus,
        }: {
            sandboxCircleId: string;
            memberId: string;
            contributionStatus: SandboxContributionStatus;
        },
        { rejectWithValue },
    ) => {
        try {
            return await submitUpdateMemberStatus(
                sandboxCircleId,
                memberId,
                contributionStatus,
            );
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to update member status.",
            );
        }
    },
);

export const updateSandboxMemberDayStatus = createAsyncThunk(
    "sandbox/updateMemberDayStatus",
    async (
        {
            sandboxCircleId,
            memberId,
            date,
            dayStatus,
        }: {
            sandboxCircleId: string;
            memberId: string;
            date: string;
            dayStatus: SandboxDayStatus;
        },
        { rejectWithValue },
    ) => {
        try {
            return await submitUpdateMemberDayStatus(
                sandboxCircleId,
                memberId,
                date,
                dayStatus,
            );
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to update contribution day.",
            );
        }
    },
);

export const createSandboxShareLink = createAsyncThunk(
    "sandbox/createShareLink",
    async (sandboxCircleId: string, { rejectWithValue }) => {
        try {
            return await submitCreateShareLink(sandboxCircleId);
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to generate share link.",
            );
        }
    },
);

export const convertSandboxCircle = createAsyncThunk(
    "sandbox/convert",
    async (sandboxCircleId: string, { rejectWithValue }) => {
        try {
            return await submitConvertSandboxCircle(sandboxCircleId);
        } catch (error: any) {
            return rejectWithValue(
                error.message || "Failed to convert sandbox circle.",
            );
        }
    },
);
